import { CancelToken } from 'axios'

import { portalApi } from '@/axios'

import { Partner, PartnerStatistics, Business, CommissionPlan, User } from '@/classes/models'

export default class PartnerService {
  /**
   * Get all partners.
   *
   * @param {PaginationRequest} parameters
   *
   * @return {Promise}
   */
  static getAll (parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(this.endpoint(), {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Set pagination
      response.totalCount = response.numberOfResults

      // Map to Partner
      response.data = response.results.map(partner => {
        return new Partner(partner)
      })

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Get partner by id.
   *
   * @param {string} partnerId
   *
   * @return {Promise}
   */
  static get (partnerId) {
    return portalApi.get(`${this.endpoint()}/${partnerId}`).then(response => {
      // Map to Partner
      return new Partner(response.data)
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Get partner businesses by partner id,
   *
   * @param {string} partnerId
   * @param {PaginationRequest} parameters
   *
   * @return {Promise}
   */
  static getBusinesses (partnerId, parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(`${this.endpoint()}/${partnerId}/businesses`, {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Set pagination
      response.totalCount = response.numberOfResults

      // Map to Business
      response.data = response.results.map(partner => {
        return new Business(partner)
      })

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Get partner commission plans by partner id,
   *
   * @param {string} partnerId
   *
   * @return {Promise}
   */
   static getCommissionPlans (partnerId) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(`${this.endpoint()}/${partnerId}/commission-plans`, {
      cancelToken: cancelToken.token
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Map to CommissionPlan
      response.data = response.map(commissionPlan => {
        return new CommissionPlan(commissionPlan)
      })

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

  /**
   * Get a commission plan by id.
   *
   * @param {string} partnerId
   * @param {string} commissionPlanId
   *
   * @return {Promise}
   */
    static getCommissionPlan (partnerId, commissionPlanId) {
    let request = portalApi.get(`${this.endpoint()}/${partnerId}/commission-plans/${commissionPlanId}`).then(response => {
      return response.data
    })

    return request.then(response => {
      // Map to CommissionPlan
      return new CommissionPlan({ id: commissionPlanId, ...response })
    }).catch(error => {
      return Promise.reject(error)
    })
  }

  /**
   * Get partner statistics.
   *
   * @param {string} partnerId
   *
   * @return {Promise}
   */
     static getStatistics (partnerId) {
      let request = portalApi.get(`${this.endpoint()}/${partnerId}/dashboard`).then(response => {
        return response.data
      })

      return request.then(response => {
        // Map to PartnerStatistics
        return new PartnerStatistics({ response })
      }).catch(error => {
        return Promise.reject(error)
      })
    }

  /**
   * Get partner users by partner id,
   *
   * @param {string} partnerId
   * @param {PaginationRequest} parameters
   *
   * @return {Promise}
   */
   static getUsers (partnerId, parameters) {
    const cancelToken = CancelToken.source()

    let request = portalApi.get(`${this.endpoint()}/${partnerId}/users`, {
      cancelToken: cancelToken.token,
      params: parameters ? parameters.getParams() : null
    }).then(response => {
      return response.data
    })

    request.then(response => {
      // Set pagination
      response.totalCount = response.numberOfResults

      // Map to User
      response.data = response.results.map(partner => {
        return new User(partner)
      })

      return response
    }).catch(error => {
      return Promise.reject(error)
    })

    return { request, cancelToken }
  }

    /**
   * Create a partner user
   *
   * @param {string} partnerId
   * @param {Object} user
   *
   * @return {Promise}
   */
    static addUser (partnerId, user) {
    return portalApi.post(`${this.endpoint()}/${partnerId}/users`, user).then(response => {
      // Map to User
      return new User(response.data)
    })
  }

  /**
   * Update a partner user
   *
   * @param {string} partnerId
   * @param {string} userId
   * @param {Object} data
   *
   * @return {Promise}
   */
  static updateUser (partnerId, userId, data) {
    return portalApi.put(`${this.endpoint()}/${partnerId}/users/${userId}`, data)
  }

  /**
   * Endpoint of API.
   *
   * @return {string}
   */
    static endpoint () {
    return '/partners'
  }
}
